/**
 * GameFlowManager - Focused on game flow decisions only
 *
 * This service is now responsible solely for determining what the user should do
 * based on their current state and entry point. It no longer handles initialization
 * logic - that has been moved to AppInitializer.
 *
 * Key responsibilities:
 * - Analyze player state (tutorial, game history, challenges)
 * - Make flow decisions (tutorial, challenge, random game, upgrade prompt)
 * - Execute those decisions by calling appropriate game store methods
 */

import { useGameStore } from "../stores/useGameStoreFacade";
import { useDailyChallengeStore } from "../stores/useDailyChallengeStore";
import { gameController } from "../game/services/GameController";
import { Logger } from "../utils/logger";
import { unifiedDataStore } from "../services/UnifiedDataStore";
import { dailyChallengesService } from "./DailyChallengesService";
import { parseEnhancedGameLink } from "./SharingService";
// Using UnifiedDataStore directly instead of StorageAdapter compatibility layer

export interface GameFlowState {
  isFirstTimeUser: boolean;
  hasGameHistory: boolean;
  hasPendingPlayerChallenge: boolean;
  hasPendingDailyChallenge: boolean;
  hasUnfinishedGame: boolean;
  hasUnfinishedDailyChallenge: boolean;
  remainingFreeGames: number;
  shouldShowTutorial: boolean;
  shouldShowNews: boolean;
}

export interface FlowDecision {
  action:
    | "tutorial"
    | "dailyChallenge"
    | "dailyChallengeCompleted"
    | "playerChallenge"
    | "randomGame"
    | "restoreGame"
    | "showMessage";
  challengeId?: string;
  startWord?: string;
  targetWord?: string;
  showUpgradePrompt?: boolean;
  message?: string;
}

interface ParsedEntryData {
  type: "challenge" | "dailychallenge";
  startWord: string;
  targetWord: string;
  challengeId?: string;
  theme?: string;
  isValid: boolean;
}

export class GameFlowManager {
  private static instance: GameFlowManager;

  // Private constructor for singleton pattern
  private constructor() {}

  public static getInstance(): GameFlowManager {
    if (!GameFlowManager.instance) {
      GameFlowManager.instance = new GameFlowManager();
    }
    return GameFlowManager.instance;
  }

  /**
   * Analyze current player state to determine what should happen next
   */
  public async analyzePlayerState(): Promise<GameFlowState> {
    const gameStore = useGameStore.getState();
    const dailyChallengeStore = useDailyChallengeStore.getState();

    Logger.debug("GameFlowManager.analyzePlayerState: Game store state", {
      initialDataLoaded: gameStore.initialDataLoaded,
      currentDailyChallenge: !!dailyChallengeStore.todaysChallenge,
      challengeId: dailyChallengeStore.todaysChallenge?.id,
      hasPlayedTodaysChallenge: dailyChallengeStore.hasPlayedToday,
      remainingFreeGames: dailyChallengeStore.remainingFreeGames,
      gameStatus: gameStore.gameStatus,
      isChallenge: gameStore.isChallenge,
      isDailyChallenge: gameStore.isDailyChallenge,
    });

    // Use the store's data instead of making independent calls
    // This ensures we're using the same data that was loaded by loadInitialData
    const remainingFreeGames = dailyChallengeStore.remainingFreeGames;

    // Check tutorial completion
    const tutorialComplete = await this.checkTutorialComplete();
    const hasGameHistory = await this.hasGameHistory();

    return {
      isFirstTimeUser: !tutorialComplete,
      hasGameHistory,
      hasPendingPlayerChallenge: gameStore.hasPendingChallenge,
      hasPendingDailyChallenge: false, // Will be set when parsing daily challenge links
      hasUnfinishedGame: await this.hasUnfinishedGame(),
      hasUnfinishedDailyChallenge: await this.hasUnfinishedDailyChallenge(),
      remainingFreeGames: remainingFreeGames,
      shouldShowTutorial: !tutorialComplete && !hasGameHistory,
      shouldShowNews: false, // News is now handled via menu notifications
    };
  }

  /**
   * Main flow decision engine based on entry point and current state
   */
  public async determineGameFlow(
    entryPoint: "landing" | "playerChallenge" | "dailyChallenge",
    challengeData?: {
      startWord?: string;
      targetWord?: string;
      challengeId?: string;
      isValid?: boolean;
    },
  ): Promise<FlowDecision> {
    const state = await this.analyzePlayerState();

    Logger.debug(" GameFlowManager.determineGameFlow:", {
      entryPoint,
      challengeData,
      state: {
        shouldShowTutorial: state.shouldShowTutorial,
        shouldShowNews: state.shouldShowNews,
        hasUnfinishedGame: state.hasUnfinishedGame,
        hasUnfinishedDailyChallenge: state.hasUnfinishedDailyChallenge,
        remainingFreeGames: state.remainingFreeGames,
      },
    });

    // Handle player challenge entry (high priority but validates history)
    if (
      entryPoint === "playerChallenge" &&
      challengeData?.startWord &&
      challengeData?.targetWord
    ) {
      // Check if this challenge has already been played
      const hasPlayedBefore = await this.hasPlayedChallenge(
        challengeData.startWord,
        challengeData.targetWord,
      );

      if (hasPlayedBefore) {
        Logger.info("Challenge already played, showing message");
        return {
          action: "showMessage",
          message: `You've already played the challenge from "${challengeData.startWord}" to "${challengeData.targetWord}". Check your game history to see your previous attempt!`,
        };
      }

      Logger.info("Starting new player challenge");
      return {
        action: "playerChallenge",
        startWord: challengeData.startWord,
        targetWord: challengeData.targetWord,
        message: "Starting new player challenge",
      };
    }

    // Handle daily challenge entry (high priority but respects tutorial/news)
    if (entryPoint === "dailyChallenge" && challengeData?.challengeId) {
      if (state.shouldShowTutorial) {
        Logger.debug(" Daily challenge entry but showing tutorial first");
        return { action: "tutorial" };
      }
      // News is no longer forced - handled via menu notifications
      // if (state.shouldShowNews) {
      //   Logger.debug(" Daily challenge entry but showing news first");
      //   return { action: "news" };
      // }

      // Check if this specific daily challenge has been completed
      const hasCompletedThisChallenge =
        await dailyChallengesService.hasCompletedSpecificChallenge(
          challengeData.challengeId,
        );

      Logger.debug(" Specific daily challenge completion check:", {
        challengeId: challengeData.challengeId,
        hasCompletedThisChallenge,
      });

      if (hasCompletedThisChallenge) {
        Logger.debug(
          " Specific daily challenge already completed, redirecting to completion view",
        );
        return {
          action: "dailyChallengeCompleted",
          challengeId: challengeData.challengeId,
          startWord: challengeData.startWord,
          targetWord: challengeData.targetWord,
        };
      }

      Logger.debug(" Starting specific daily challenge from entry");
      return {
        action: "dailyChallenge",
        challengeId: challengeData.challengeId,
        startWord: challengeData.startWord,
        targetWord: challengeData.targetWord,
      };
    }

    // Handle landing page entry
    if (entryPoint === "landing") {
      // First priority: Tutorial for new users
      if (state.shouldShowTutorial) {
        Logger.debug(" Showing tutorial for new user");
        return { action: "tutorial" };
      }

      // News handling moved to menu notifications system
      // if (state.shouldShowNews) {
      //   Logger.debug(" Showing news");
      //   return { action: "news" };
      // }

      // Second priority: Restore accessible unfinished daily challenges
      if (state.hasUnfinishedDailyChallenge) {
        Logger.debug(" Restoring unfinished daily challenge");
        return { action: "restoreGame" };
      }

      // Third priority: Restore unfinished regular games
      if (state.hasUnfinishedGame) {
        Logger.debug(" Restoring unfinished regular game");
        return { action: "restoreGame" };
      }

      // Fourth priority: New daily challenge if not completed (regardless of game history)
      const dailyChallengeStore = useDailyChallengeStore.getState();
      const todaysChallenge = dailyChallengeStore.todaysChallenge;

      Logger.debug(" Checking daily challenge priority:", {
        todaysChallenge: !!todaysChallenge,
        challengeId: todaysChallenge?.id,
        startWord: todaysChallenge?.startWord,
        targetWord: todaysChallenge?.targetWord,
      });

      if (todaysChallenge) {
        // Check persistent storage FIRST to get the authoritative completion status
        // This fixes the bug where users are taken to completed daily challenges after sync
        const hasCompletedPersistent =
          await dailyChallengesService.hasCompletedTodaysChallenge();
        const hasPlayedToday = dailyChallengeStore.hasPlayedToday;

        Logger.debug(" Daily challenge completion check:", {
          hasPlayedToday,
          hasCompletedPersistent,
          willAutoStart: !hasCompletedPersistent,
        });

        // Use persistent storage as the authoritative source
        if (!hasCompletedPersistent) {
          Logger.debug(" Starting today's daily challenge");
          return {
            action: "dailyChallenge",
            challengeId: todaysChallenge.id,
            startWord: todaysChallenge.startWord,
            targetWord: todaysChallenge.targetWord,
          };
        } else {
          Logger.debug(
            " Daily challenge already completed, skipping auto-start",
          );
        }
      }

      // Fifth priority: Random game (unlimited in local-only mode)
      console.log("🎮 Starting random game (local-only mode)");
      return { action: "randomGame" };
    }

    // Default fallback
    Logger.debug(" Default fallback to random game");
    return { action: "randomGame" };
  }

  /**
   * Parse URL parameters to extract entry information
   */
  public parseEntryUrl = (url: string): ParsedEntryData | null => {
    try {
      // Try enhanced format first (with type parameter)
      const enhancedData = parseEnhancedGameLink(url);

      if (
        enhancedData &&
        enhancedData.type &&
        enhancedData.startWord &&
        enhancedData.targetWord
      ) {
        return {
          type: enhancedData.type as "challenge" | "dailychallenge",
          startWord: enhancedData.startWord.trim(),
          targetWord: enhancedData.targetWord.trim(),
          challengeId: enhancedData.challengeId,
          theme: enhancedData.theme,
          isValid: true,
        };
      }
      return null;
    } catch (error) {
      console.error("🎮 GameFlowManager: Error parsing entry URL:", error);
      return null;
    }
  };

  /**
   * Execute the flow decision
   */
  public async executeFlowDecision(decision: FlowDecision): Promise<void> {
    console.log(
      "🚀 GameFlowManager.executeFlowDecision called with:",
      decision,
    );
    const gameStore = useGameStore.getState();

    Logger.debug(" GameFlowManager.executeFlowDecision:", decision);

    switch (decision.action) {
      case "tutorial":
        console.log("🎮 GameFlowManager: Executing tutorial case");
        Logger.debug(" Executing: tutorial");
        // Start a genuinely random game (NEVER the daily — skipDailyPriority)
        // for the guided walkthrough; TutorialContext overlays guidance on it.
        // isTutorial marks its mode (history badge, telemetry "tutorial",
        // Q.E.D.); otherwise it is recorded like any other game.
        await gameController.startRandomGame({
          skipDailyPriority: true,
          isTutorial: true,
        });
        console.log("🎮 GameFlowManager: Tutorial game started");
        break;

      // Note: "news" action removed - news is now handled via menu notifications

      case "playerChallenge":
        Logger.debug(" Executing: playerChallenge");
        if (decision.startWord && decision.targetWord) {
          await gameStore.startChallengeGame(
            decision.startWord,
            decision.targetWord,
          );
        }
        break;

      case "dailyChallenge":
        Logger.debug(" Executing: dailyChallenge");
        await gameStore.startDailyChallengeGame();
        break;

      case "dailyChallengeCompleted":
        Logger.debug(" Executing: dailyChallengeCompleted");
        // Show the completed challenge report instead of starting the game
        // For now, we'll show a message and then navigate to the daily challenges view
        if (decision.challengeId && decision.startWord && decision.targetWord) {
          const message = `This daily challenge (${decision.startWord} → ${decision.targetWord}) has already been completed!`;
          gameStore.showGlobalMessage(message);
          // TODO: Navigate to specific challenge report if needed
        }
        break;

      case "randomGame":
        Logger.debug(" Executing: randomGame");
        // Free game consumption is now handled in startGame function
        await gameStore.startGame();
        break;

      case "restoreGame":
        Logger.debug(" Executing: restoreGame");
        // Game restoration is handled automatically by gameStore.loadInitialData
        break;

      case "showMessage": {
        Logger.debug("🎮 Executing: showMessage");
        Logger.info("🎮 Challenge info message:", decision.message);
        // Show proper UI feedback using the global message system
        if (decision.message) {
          gameStore.showGlobalMessage(decision.message);
        }

        // After showing the message, continue with normal game flow
        // This gives users a proper game instead of leaving them hanging
        const fallbackDecision = await this.determineGameFlow("landing");
        if (fallbackDecision.action !== "showMessage") {
          // Prevent infinite loops by only executing non-message actions
          await this.executeFlowDecision(fallbackDecision);
        }
        break;
      }
    }
  }

  // Helper methods
  private async checkTutorialComplete(): Promise<boolean> {
    try {
      // Use UnifiedDataStore instead of AsyncStorage for consistency
      return await unifiedDataStore.isTutorialComplete();
    } catch {
      return false;
    }
  }

  private async hasGameHistory(): Promise<boolean> {
    try {
      const data = await unifiedDataStore.getData();
      const history = data.gameHistory;
      return history.length > 0;
    } catch {
      return false;
    }
  }

  private async hasUnfinishedGame(): Promise<boolean> {
    try {
      const data = await unifiedDataStore.getData();
      const savedGame = data.currentGames.regular; // Regular game
      return (
        savedGame !== null &&
        savedGame.gameStatus === "playing" &&
        savedGame.isDailyChallenge !== true // Exclude daily challenges
      );
    } catch {
      return false;
    }
  }

  private async hasUnfinishedDailyChallenge(): Promise<boolean> {
    try {
      const data = await unifiedDataStore.getData();
      const savedGame = data.currentGames.regular; // Daily challenges are stored in regular slot, not challenge slot

      if (
        savedGame === null ||
        savedGame.gameStatus !== "playing" ||
        savedGame.isDailyChallenge !== true ||
        !savedGame.currentDailyChallengeId
      ) {
        return false;
      }

      // Check if the saved daily challenge is still valid for today
      const todaysChallenge = dailyChallengesService.getTodaysChallenge();

      // If there's no today's challenge or the saved challenge is not today's challenge,
      // clear the expired challenge
      if (
        !todaysChallenge ||
        savedGame.currentDailyChallengeId !== todaysChallenge.id
      ) {
        // Clear expired daily challenge
        await unifiedDataStore.clearCurrentGame("regular");
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Check if a specific player challenge (start/target word combination) has been played before
   */
  private async hasPlayedChallenge(
    startWord: string,
    targetWord: string,
  ): Promise<boolean> {
    try {
      const data = await unifiedDataStore.getData();
      const history = data.gameHistory;
      // Filter for regular player challenges (not daily challenges)
      const challengeHistory = history.filter(
        (game) => !game.isDailyChallenge, // Regular challenges are NOT daily challenges
      );

      // Check if this exact start/target combination has been played
      const hasPlayed = challengeHistory.some(
        (game) =>
          game.startWord?.toLowerCase() === startWord.toLowerCase() &&
          game.targetWord?.toLowerCase() === targetWord.toLowerCase(),
      );

      Logger.debug("🎮 GameFlowManager: Challenge history check:", {
        startWord,
        targetWord,
        hasPlayed,
        totalChallenges: challengeHistory.length,
      });

      return hasPlayed;
    } catch (error) {
      Logger.debug(
        "🎮 GameFlowManager: Error checking challenge history:",
        error,
      );
      return false; // Default to allow playing if we can't check
    }
  }
}

export const gameFlowManager = GameFlowManager.getInstance();
